import { useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  type AuthProvider,
} from "firebase/auth";
import { firebaseAuth } from "../../firebase";
import { sendAuthenticatedRequest, sendVerificationEmailRequest } from "../../common/functions";
import { storePromotionCode } from "../../common/promotionCodes";
import { useMiscStore } from "../../common/stateDefs";
import {
  claimButtonLabel,
  signupHeadline,
  signupSubhead,
  successHeadline,
  successSubhead,
  type ExitIntentPromoView,
} from "./exitIntentCopy";
import { rememberAccountEmail } from "./exitIntentTriggers";

type Mode = "signup-email" | "signup-password" | "signin" | "success";
type SsoProvider = "google" | "microsoft";

type Props = {
  open: boolean;
  onClose: () => void;
  promo: ExitIntentPromoView;
  offerImageUrl: string;
  offerId: string;
  initialEmail?: string;
  startOnSignIn?: boolean;
};

const ssoMessage = (code: string): string => {
  switch (code) {
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "";
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in window. Allow pop-ups for this site and try again.";
    case "auth/unauthorized-domain":
      return "This site is not yet authorized for that sign-in method. Use email and password.";
    case "auth/account-exists-with-different-credential":
      return "This address already has a Kono account. Sign in with the method you used originally.";
    case "auth/email-already-in-use":
      return "An account with this email already exists. Sign in instead.";
    case "auth/wrong-password":
    case "auth/invalid-credential":
    case "auth/user-not-found":
      return "Incorrect email or password.";
    case "auth/weak-password":
      return "Use at least 8 characters.";
    default:
      return "Something went wrong. Please try again.";
  }
};

const GoogleMark = () => (
  <svg className="h-5 w-5 shrink-0" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.2 0 10-2 13.5-5.2l-6.2-5.2C29.3 35.3 26.8 36 24 36c-5.3 0-9.7-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-4.1 5.6-7.3 6.6l6.2 5.2C36.5 41.3 44 36 44 24c0-1.3-.1-2.3-.4-3.5z"
    />
  </svg>
);

const MicrosoftMark = () => (
  <svg className="h-5 w-5 shrink-0" viewBox="0 0 23 23" aria-hidden="true">
    <path fill="#F25022" d="M1 1h10v10H1z" />
    <path fill="#7FBA00" d="M12 1h10v10H12z" />
    <path fill="#00A4EF" d="M1 12h10v10H1z" />
    <path fill="#FFB900" d="M12 12h10v10H12z" />
  </svg>
);

const buildProvider = (which: SsoProvider): AuthProvider => {
  if (which === "google") {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    return provider;
  }
  const provider = new OAuthProvider("microsoft.com");
  provider.addScope("email");
  provider.addScope("openid");
  provider.addScope("profile");
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
};

const emailLooksValid = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const passwordHint = (password: string): { text: string; error: boolean } => {
  if (!password) {
    return { text: "Use at least 8 characters", error: false };
  }
  if (password.length < 8) {
    const need = 8 - password.length;
    return {
      text: `Need ${need} more character${need === 1 ? "" : "s"} (min. 8)`,
      error: true,
    };
  }
  return { text: "Use at least 8 characters", error: false };
};

export function ExitIntentAuthModal({
  open,
  onClose,
  promo,
  offerImageUrl,
  offerId,
  initialEmail = "",
  startOnSignIn = false,
}: Props) {
  const [mode, setMode] = useState<Mode>(
    startOnSignIn ? "signin" : "signup-email",
  );
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ssoBusy, setSsoBusy] = useState<SsoProvider | null>(null);
  const [error, setError] = useState("");
  const [imageFailed, setImageFailed] = useState(false);
  const setCompanyDetailsRequired = useMiscStore(
    (state) => state.setCompanyDetailsRequired,
  );
  const [pendingCompanyDetails, setPendingCompanyDetails] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(startOnSignIn ? "signin" : "signup-email");
    setEmail(initialEmail);
    setPassword("");
    setShowPassword(false);
    setError("");
    setBusy(false);
    setSsoBusy(null);
    setPendingCompanyDetails(false);
    setImageFailed(false);
  }, [open, initialEmail, startOnSignIn]);

  if (!open) return null;

  const finish = () => {
    if (pendingCompanyDetails) setCompanyDetailsRequired(true);
    onClose();
  };

  const claimOffer = async () => {
    const user = firebaseAuth.currentUser;
    if (user?.email) rememberAccountEmail(user.email);
    try {
      sessionStorage.removeItem("signupPromoPending");
      sessionStorage.removeItem("signupPromoOfferId");
      const response = await sendAuthenticatedRequest(
        `${process.env.REACT_APP_BF_API_BASE_URL}/promotions/claim`,
        { method: "POST", jsonBody: { offerId } },
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.code) {
        storePromotionCode({
          promotionCode: String(data.code),
          confirmationMessage: String(data.offerText || promo.offerText),
        });
      }
    } catch {
      // Claiming is best-effort; they are already signed in.
    }
    setMode("success");
  };

  const handleSso = (which: SsoProvider) => {
    if (ssoBusy || busy) return;
    setError("");
    setSsoBusy(which);
    firebaseAuth.useDeviceLanguage();
    signInWithPopup(firebaseAuth, buildProvider(which))
      .then(async (uc) => {
        const info = getAdditionalUserInfo(uc);
        if (info?.isNewUser) setPendingCompanyDetails(true);
        await claimOffer();
      })
      .catch((err) => {
        setError(ssoMessage(err?.code || "auth/unknown"));
      })
      .finally(() => setSsoBusy(null));
  };

  const continueEmail = () => {
    if (!emailLooksValid(email)) {
      setError("Enter a work email.");
      return;
    }
    setError("");
    setMode("signup-password");
  };

  const submitSignup = () => {
    if (password.length < 8) return;
    setBusy(true);
    setError("");
    createUserWithEmailAndPassword(firebaseAuth, email.trim(), password)
      .then(async () => {
        try {
          await sendVerificationEmailRequest();
        } catch {
          /* they can verify later */
        }
        setPendingCompanyDetails(true);
        await claimOffer();
      })
      .catch((err) => {
        if (err?.code === "auth/email-already-in-use") {
          setMode("signin");
          setError("An account with this email already exists. Sign in instead.");
        } else {
          setError(ssoMessage(err?.code || "auth/unknown"));
        }
      })
      .finally(() => setBusy(false));
  };

  const submitSignIn = () => {
    if (!emailLooksValid(email) || password.length < 8) return;
    setBusy(true);
    setError("");
    signInWithEmailAndPassword(firebaseAuth, email.trim(), password)
      .then(async () => {
        await claimOffer();
      })
      .catch((err) => {
        if (err?.code === "auth/user-not-found") {
          setMode("signup-password");
          setError("");
        } else {
          setError(ssoMessage(err?.code || "auth/unknown"));
        }
      })
      .finally(() => setBusy(false));
  };

  const hint = passwordHint(password);
  const showSso = process.env.REACT_APP_ENABLE_SSO === "true";
  const primaryEnabled =
    mode === "signup-email"
      ? emailLooksValid(email) && !busy
      : mode === "signin"
        ? emailLooksValid(email) && password.length >= 8 && !busy
        : password.length >= 8 && !busy;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="relative flex h-[min(36rem,90vh)] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl">
        <button
          type="button"
          className="absolute left-3 top-3 z-10 text-lg leading-none text-slate-400 hover:text-slate-600"
          aria-label="Close"
          onClick={finish}
        >
          ×
        </button>

        <div className="relative hidden w-1/2 overflow-hidden bg-[repeating-linear-gradient(135deg,#e8e8e8_0px,#e8e8e8_10px,#f3f3f3_10px,#f3f3f3_20px)] md:block">
          {offerImageUrl && !imageFailed ? (
            <img
              src={offerImageUrl}
              alt=""
              className="h-full w-full object-cover"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <div className="flex h-full items-center justify-center px-10 text-center text-sm leading-relaxed text-slate-400">
              Inconel round bar,
              <br />
              packaged &amp; loaded on
              <br />
              a flatbed truck
              <br />
              (white-glove delivery)
            </div>
          )}
        </div>

        <div className="flex w-full flex-col justify-center px-8 py-10 md:w-1/2 md:px-12">
          {mode === "success" ? (
            <div className="text-center">
              <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-[#00062e] text-xl text-white">
                ✓
              </div>
              <h2 className="text-3xl font-bold leading-tight text-[#00062e]">
                {successHeadline(promo)}
              </h2>
              <p className="mt-3 text-sm text-slate-500">
                {successSubhead(promo)}
              </p>
              <button
                type="button"
                className="mt-8 w-full rounded-full bg-[#00062e] px-4 py-3 text-sm font-semibold text-white hover:bg-[#1a2050]"
                onClick={finish}
              >
                Start Shopping
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-3xl font-bold leading-tight text-[#00062e]">
                {mode === "signin" ? "Welcome Back" : signupHeadline(promo)}
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                {mode === "signin"
                  ? "Sign in to your Kono account."
                  : signupSubhead(promo)}
              </p>

              {showSso && (
                <div className="mt-6 space-y-3">
                  <button
                    type="button"
                    disabled={!!ssoBusy || busy}
                    onClick={() => handleSso("google")}
                    className="flex w-full items-center justify-center gap-3 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                  >
                    <GoogleMark />
                    {ssoBusy === "google"
                      ? "Opening Google…"
                      : "Log in with Google Account"}
                  </button>
                  <button
                    type="button"
                    disabled={!!ssoBusy || busy}
                    onClick={() => handleSso("microsoft")}
                    className="flex w-full items-center justify-center gap-3 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                  >
                    <MicrosoftMark />
                    {ssoBusy === "microsoft"
                      ? "Opening Microsoft…"
                      : "Log in with Microsoft Account"}
                  </button>
                </div>
              )}

              {showSso && (
                <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
                  <div className="h-px flex-1 bg-slate-200" />
                  or
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
              )}

              <label className="sr-only" htmlFor="exit-intent-email">
                {mode === "signin" ? "Email" : "Work email"}
              </label>
              <div className="relative">
                <input
                  id="exit-intent-email"
                  type="email"
                  autoComplete="email"
                  placeholder={mode === "signin" ? "Email" : "Work email"}
                  value={email}
                  readOnly={mode === "signup-password"}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full rounded-full border border-slate-200 px-4 py-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-slate-400 ${
                    mode === "signup-password" ? "bg-slate-50 pr-12" : ""
                  }`}
                />
                {mode === "signup-password" && (
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      aria-hidden="true"
                    >
                      <rect x="5" y="11" width="14" height="10" rx="2" />
                      <path d="M8 11V8a4 4 0 018 0v3" />
                    </svg>
                  </span>
                )}
              </div>

              {(mode === "signup-password" || mode === "signin") && (
                <>
                  <label className="sr-only" htmlFor="exit-intent-password">
                    {mode === "signin" ? "Password" : "Create a password"}
                  </label>
                  <div className="relative mt-3">
                    <input
                      id="exit-intent-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete={
                        mode === "signin" ? "current-password" : "new-password"
                      }
                      placeholder={
                        mode === "signin" ? "Password" : "Create a password"
                      }
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full rounded-full border px-4 py-3 pr-12 text-sm text-slate-800 outline-none placeholder:text-slate-400 ${
                        hint.error
                          ? "border-red-400 focus:border-red-500"
                          : "border-slate-200 focus:border-slate-400"
                      }`}
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        aria-hidden="true"
                      >
                        {showPassword ? (
                          <>
                            <path d="M3 3l18 18" />
                            <path d="M10.6 10.6a2 2 0 002.8 2.8" />
                            <path d="M9.9 5.1A10.6 10.6 0 0121 12c-.7 1.2-1.6 2.3-2.7 3.2" />
                            <path d="M6.1 6.1A10.6 10.6 0 003 12c1.8 3.4 5.2 6 9 6 1.3 0 2.5-.3 3.6-.8" />
                          </>
                        ) : (
                          <>
                            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                  {mode === "signup-password" && (
                    <p
                      className={`mt-2 text-xs ${
                        hint.error ? "text-red-500" : "text-slate-400"
                      }`}
                    >
                      {hint.error ? `× ${hint.text}` : hint.text}
                    </p>
                  )}
                </>
              )}

              {!!error && (
                <p className="mt-3 text-sm text-red-600">{error}</p>
              )}

              <button
                type="button"
                disabled={!primaryEnabled}
                onClick={() => {
                  if (mode === "signup-email") continueEmail();
                  else if (mode === "signin") submitSignIn();
                  else submitSignup();
                }}
                className="mt-5 w-full rounded-full bg-[#00062e] px-4 py-3 text-sm font-semibold text-white hover:bg-[#1a2050] disabled:bg-slate-300"
              >
                {mode === "signup-email"
                  ? "Continue"
                  : mode === "signin"
                    ? "Sign In"
                    : claimButtonLabel(promo)}
              </button>

              <p className="mt-4 text-center text-sm text-slate-500">
                {mode === "signin" ? (
                  <>
                    Don&apos;t have an account?{" "}
                    <button
                      type="button"
                      className="font-medium text-slate-800 underline"
                      onClick={() => {
                        setMode("signup-email");
                        setPassword("");
                        setError("");
                      }}
                    >
                      Sign up
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <button
                      type="button"
                      className="font-medium text-slate-800 underline"
                      onClick={() => {
                        setMode("signin");
                        setPassword("");
                        setError("");
                      }}
                    >
                      Sign in
                    </button>
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
